/**
 * useDraggableDialog.js - pop-up windows the person can move (Sharif,
 * 10/9/26: "when any window pops up make it so that the user can move that
 * window on the screen"). One hook for every dialog: the shared Modal and
 * the dialogs that draw their own overlay.
 *
 *   const drag = useDraggableDialog(open);
 *   <div ref={drag.ref} style={{ ...panel, ...drag.style }} onPointerDown={drag.onPointerDown}>
 *     <div data-dialog-handle>title bar</div> ...
 *
 * Dragging starts on an element marked data-dialog-handle (the title / header
 * bar), never on a button, field or link inside it, so those still click.
 * Pointer Events, so mouse, pen and touch all work. While dragging the panel
 * is moved in the DOM once per animation frame (no re-render of a large form
 * per pixel) and the position is kept when the pointer comes up. At least
 * KEEP_VISIBLE px of the window stay on screen, and its top edge never goes
 * above the viewport (the handle stays reachable). The position resets each
 * time the dialog opens. Text is not selected while dragging.
 *
 * While the pointer moves, the panel is only translated (a transform: no
 * layout, the compositor moves it - round 4, "buttery"); when it comes up the
 * position is written as left / top with position: relative and the
 * transform cleared, because a transform left in place would make the panel
 * the containing block of any fixed-position child. `transform: true` is for
 * a panel that is itself fixed (the AI chat), which keeps its transform.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const KEEP_VISIBLE = 60;
const INTERACTIVE = 'button, input, select, textarea, a, label, [role="button"], [contenteditable="true"], [data-no-drag]';

export function useDraggableDialog(open = true, { transform = false, ref: outerRef } = {}) {
  const innerRef = useRef(null);
  const ref = outerRef || innerRef;
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const posRef = useRef(pos);
  posRef.current = pos;

  // a dialog opens where it was designed to
  useEffect(() => {
    if (open) setPos({ x: 0, y: 0 });
  }, [open]);

  // during a drag: a transform from where the drag started (cheap)
  const preview = useCallback(
    (el, x, y, from) => {
      el.style.transform = transform ? `translate(${x}px, ${y}px)` : `translate(${x - from.x}px, ${y - from.y}px)`;
    },
    [transform]
  );
  // at the end: the real position
  const place = useCallback(
    (el, x, y) => {
      if (transform) el.style.transform = `translate(${x}px, ${y}px)`;
      else {
        el.style.transform = '';
        el.style.position = el.style.position || 'relative';
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
      }
    },
    [transform]
  );

  const onPointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      const el = ref.current;
      const handle = e.target && e.target.closest ? e.target.closest('[data-dialog-handle]') : null;
      if (!el || !handle || !el.contains(handle) || e.target.closest(INTERACTIVE)) return;
      e.preventDefault(); // no text selection, no native drag
      const start = { px: e.clientX, py: e.clientY, ...posRef.current };
      const rect = el.getBoundingClientRect();
      // where the panel would sit at offset 0
      const base = { left: rect.left - start.x, top: rect.top - start.y, w: rect.width };
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const clamp = (x, y) => ({
        x: Math.min(vw - KEEP_VISIBLE - base.left, Math.max(KEEP_VISIBLE - base.w - base.left, x)),
        y: Math.min(vh - KEEP_VISIBLE - base.top, Math.max(-base.top, y)),
      });
      let next = { x: start.x, y: start.y };
      let frame = 0;
      const prevSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';
      el.setAttribute('data-dragging', '');
      const move = (ev) => {
        next = clamp(start.x + ev.clientX - start.px, start.y + ev.clientY - start.py);
        if (frame) return;
        const run = () => {
          frame = 0;
          preview(el, next.x, next.y, start);
        };
        if (typeof window.requestAnimationFrame === 'function') frame = window.requestAnimationFrame(run);
        else run();
      };
      const up = () => {
        document.removeEventListener('pointermove', move);
        document.removeEventListener('pointerup', up);
        document.removeEventListener('pointercancel', up);
        if (frame && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
        frame = 0;
        place(el, next.x, next.y);
        document.body.style.userSelect = prevSelect;
        el.removeAttribute('data-dragging');
        setPos(next);
      };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', up);
      document.addEventListener('pointercancel', up);
    },
    [ref, place, preview]
  );

  const moved = pos.x !== 0 || pos.y !== 0;
  let style;
  if (transform) style = moved ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined;
  else style = { position: 'relative', left: pos.x, top: pos.y };
  return { ref, style, onPointerDown, pos };
}
